import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Card, CardContent } from '../ui/card';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Gauge as GaugeIcon,
  Loader2,
  MapPin,
  PauseCircle,
  PlayCircle,
  WifiOff,
  Zap,
} from 'lucide-react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { getWebSocketClient } from '../../utils/websocket-client';
import {
  computeVfdValues,
  findByRole,
  getDriveState,
  VFD_STATUS_COLORS,
  type VfdComputedValue,
  type VfdDrive,
  type VfdDriveState,
  vfdApi,
} from '../../utils/vfd';

interface VFDDrivesPanelProps {
  clientId: string;
  token: string;
  /** Pre-loaded drives (from the Live PLC Data wrapper) — avoids a second fetch. */
  initialDrives?: VfdDrive[];
}

type LiveSnapshot = {
  values: Record<string, number | null>;
  raws: Record<string, number>;
  errors: Record<string, string>;
  comm: string;
  timestamp: number;
};

type RuntimeStatus = { connected: boolean; comm?: string; error?: string | null; reconnecting?: boolean; at: number };

type TrendPoint = {
  t: number;
  frequency: number | null;
  current: number | null;
  power: number | null;
  voltage: number | null;
  pf: number | null;
};

// Categorical slots (fixed order, never cycled).
const C_FREQ = '#2a78d6';
const C_CURRENT = '#eb6834';
const C_POWER = '#1baf7a';
const C_SPEED = '#4a3aa7';

const TREND_SERIES: { key: 'frequency' | 'current' | 'power'; label: string; color: string }[] = [
  { key: 'frequency', label: 'Output frequency', color: C_FREQ },
  { key: 'current', label: 'Output current', color: C_CURRENT },
  { key: 'power', label: 'Output power', color: C_POWER },
];

const TREND_WINDOW_MS = 10 * 60 * 1000;
// A missed Modbus reply must not blank a running drive: keep each register's last good
// reading this long before showing it as failed / the drive as "No communication".
const HOLD_LAST_GOOD_MS = 12_000;
const MAX_TREND_POINTS = 900;

const STATE_META: Record<VfdDriveState, { label: string; color: string; icon: any }> = {
  running: { label: 'Running', color: VFD_STATUS_COLORS.good, icon: PlayCircle },
  stopped: { label: 'Stopped', color: VFD_STATUS_COLORS.neutral, icon: PauseCircle },
  fault: { label: 'Fault', color: VFD_STATUS_COLORS.critical, icon: AlertTriangle },
  offline: { label: 'No communication', color: VFD_STATUS_COLORS.warning, icon: WifiOff },
  waiting: { label: 'Waiting for data', color: VFD_STATUS_COLORS.neutral, icon: Loader2 },
};

const isNum = (v: any): v is number => typeof v === 'number' && Number.isFinite(v);
const fmt = (v: number | null | undefined, d = 1) =>
  isNum(v) ? v.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

const timeAgo = (ts?: number) => {
  if (!ts) return 'never';
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 2) return 'just now';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return new Date(ts).toLocaleTimeString();
};

// ------------------------------------------------------------------ motion helpers

/** Eases from the previous value to the new one, so readings count rather than jump. */
function useTween(target: number | null, duration = 650): number | null {
  const reduce = useReducedMotion();
  const [value, setValue] = useState<number | null>(target);
  const fromRef = useRef<number | null>(target);
  const frameRef = useRef<number>(0);

  useEffect(() => {
    cancelAnimationFrame(frameRef.current);
    const from = fromRef.current;
    if (!isNum(target) || !isNum(from) || reduce) {
      fromRef.current = target;
      setValue(target);
      return;
    }
    const start = performance.now();
    const step = (now: number) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      const v = from + (target - from) * eased;
      fromRef.current = v;
      setValue(v);
      if (p < 1) frameRef.current = requestAnimationFrame(step);
    };
    frameRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameRef.current);
  }, [target, duration, reduce]);

  return value;
}

function AnimatedNumber({ value, decimals = 1, className }: { value: number | null; decimals?: number; className?: string }) {
  const v = useTween(value);
  return <span className={className}>{fmt(v, decimals)}</span>;
}

const sectionVariants = {
  hidden: { opacity: 0, y: 14 },
  show: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.35, ease: [0.22, 1, 0.36, 1] as any } }),
};

function Section({ i, children, className }: { i: number; children: any; className?: string }) {
  return (
    <motion.div custom={i} variants={sectionVariants} initial="hidden" animate="show" className={className}>
      {children}
    </motion.div>
  );
}

// ------------------------------------------------------------------ small pieces

function StatePill({ state, size = 'sm' }: { state: VfdDriveState; size?: 'sm' | 'lg' }) {
  const meta = STATE_META[state];
  const Icon = meta.icon;
  const pulse = state === 'running' || state === 'fault';
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border font-semibold ${size === 'lg' ? 'px-3 py-1.5 text-sm' : 'px-2 py-0.5 text-xs'}`}
      style={{ color: meta.color, borderColor: `${meta.color}55`, backgroundColor: `${meta.color}14` }}
    >
      {pulse ? (
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 motion-reduce:hidden" style={{ backgroundColor: meta.color }} />
          <span className="relative inline-flex h-2 w-2 rounded-full" style={{ backgroundColor: meta.color }} />
        </span>
      ) : (
        <Icon className={`${size === 'lg' ? 'h-4 w-4' : 'h-3.5 w-3.5'} ${state === 'waiting' ? 'animate-spin' : ''}`} />
      )}
      {meta.label}
    </span>
  );
}

/** Tiny fan for drive cards — spins while the motor runs. */
function MiniFan({ spinning, color }: { spinning: boolean; color: string }) {
  return (
    <svg viewBox="0 0 24 24" className={`h-5 w-5 ${spinning ? 'animate-spin' : ''}`} style={{ animationDuration: '0.9s', color }} aria-hidden>
      <circle cx="12" cy="12" r="2.2" fill="currentColor" />
      {[0, 120, 240].map((a) => (
        <path key={a} d="M12 12 C 13 6, 18 5, 19 8 C 19.5 10, 15 11.5, 12 12 Z" fill="currentColor" opacity={0.85} transform={`rotate(${a} 12 12)`} />
      ))}
    </svg>
  );
}

// Round axis top: 1, 2, 2.5, 5 x 10^n.
const niceCeil = (v: number) => {
  if (!(v > 0)) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / p;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * p;
};
// Decimals needed to print a tick step exactly (5 -> 0, 2.5 -> 1, 0.25 -> 2).
const tickDecimals = (step: number) => {
  let d = 0;
  while (d < 4 && Math.abs(step * 10 ** d - Math.round(step * 10 ** d)) > 1e-6) d++;
  return d;
};

/**
 * Physical readings (Hz, A, kW, V) are drawn from zero with a minimum height
 * (minTop, ~10% of the rating). Otherwise a stopped drive's sensor noise
 * (e.g. 0.07–0.10 kW) gets auto-zoomed into what looks like large swings.
 */
const valueDomain = (vals: number[], minTop: number) => {
  const max = vals.length ? Math.max(...vals) : 0;
  const min = vals.length ? Math.min(...vals) : 0;
  // Idle / near-zero readings (sensor noise on a stopped drive): draw from zero so
  // the noise stays a flat line near the bottom.
  if (max <= minTop && min >= 0) {
    const top = niceCeil(minTop);
    const step = top / 4;
    return { bottom: 0, top, step, ticks: [0, step, step * 2, step * 3, top] };
  }
  // Operating: zoom to the data, but never to a span smaller than minTop, so small
  // jitter is not exaggerated while real swings stay visible.
  const span = Math.max(max - min, minTop);
  const step = niceCeil(span / 4);
  const pad = (span - (max - min)) / 2 + step * 0.25;
  let bottom = Math.floor((min - pad) / step) * step;
  if (min >= 0 && bottom < 0) bottom = 0;
  const top = Math.ceil((max + pad) / step) * step;
  const ticks: number[] = [];
  for (let v = bottom; v <= top + step / 2 && ticks.length < 8; v += step) ticks.push(Number(v.toFixed(6)));
  return { bottom, top, step, ticks };
};

function Sparkline({ data, color, minTop = 1 }: { data: (number | null)[]; color: string; minTop?: number }) {
  const pts = data.filter(isNum).slice(-60);
  if (pts.length < 2) return <div className="h-8" />;
  const { top, bottom } = valueDomain(pts, minTop);
  const min = bottom;
  const range = top - bottom || 1;
  const w = 120;
  const h = 32;
  const line = pts.map((v, i) => `${(i / (pts.length - 1)) * w},${h - 3 - ((v - min) / range) * (h - 6)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="h-8 w-full" aria-hidden>
      <polygon points={`0,${h} ${line} ${w},${h}`} fill={color} opacity={0.1} />
      <polyline points={line} fill="none" stroke={color} strokeWidth={1.75} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/** 240° arc meter with gradient fill, minor ticks and a glowing tip that tracks the value. */
function ArcGauge({
  value, max, label, unit, decimals = 1, color = C_FREQ, sub, id,
}: { value: number | null; max: number; label: string; unit: string; decimals?: number; color?: string; sub?: string; id: string }) {
  const size = 220;
  const stroke = 14;
  const r = (size - stroke) / 2 - 10;
  const cx = size / 2;
  const cy = size / 2;
  const start = 150;
  const sweep = 240;
  const tweened = useTween(value);
  const frac = isNum(tweened) && max > 0 ? clamp01(Math.abs(tweened) / max) : 0;
  const polar = (deg: number, radius = r) => {
    const rad = (deg * Math.PI) / 180;
    return [cx + radius * Math.cos(rad), cy + radius * Math.sin(rad)];
  };
  const arc = (from: number, to: number) => {
    const [x1, y1] = polar(from);
    const [x2, y2] = polar(to);
    return `M ${x1} ${y1} A ${r} ${r} 0 ${to - from > 180 ? 1 : 0} 1 ${x2} ${y2}`;
  };
  const arcLength = (r * sweep * Math.PI) / 180;
  const tipDeg = start + sweep * frac;
  const [tx, ty] = polar(tipDeg);
  const hasValue = isNum(value);

  return (
    <div className="flex flex-col items-center">
      <svg viewBox={`0 0 ${size} ${size * 0.84}`} className="w-full max-w-[240px]" role="img" aria-label={`${label}: ${fmt(value, decimals)} ${unit} of ${max} ${unit}`}>
        <defs>
          <linearGradient id={`g-${id}`} x1="0" y1="1" x2="1" y2="0">
            <stop offset="0%" stopColor={color} stopOpacity={0.55} />
            <stop offset="100%" stopColor={color} />
          </linearGradient>
          <filter id={`glow-${id}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        {/* minor ticks */}
        {Array.from({ length: 41 }, (_, i) => {
          const t = i / 40;
          const deg = start + sweep * t;
          const major = i % 10 === 0;
          const [x1, y1] = polar(deg, r + stroke / 2 + 3);
          const [x2, y2] = polar(deg, r + stroke / 2 + (major ? 9 : 5));
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="currentColor" strokeOpacity={t <= frac && hasValue ? 0.55 : 0.18} strokeWidth={major ? 1.5 : 1} className="text-foreground" />;
        })}
        <path d={arc(start, start + sweep)} fill="none" stroke="currentColor" strokeOpacity={0.1} strokeWidth={stroke} strokeLinecap="round" className="text-foreground" />
        <path
          d={arc(start, start + sweep)}
          fill="none"
          stroke={`url(#g-${id})`}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={arcLength}
          strokeDashoffset={arcLength * (1 - frac)}
          opacity={frac > 0.002 ? 1 : 0}
        />
        {hasValue && frac > 0.002 && (
          <g filter={`url(#glow-${id})`}>
            <circle cx={tx} cy={ty} r={9} fill={color} opacity={0.35} />
            <circle cx={tx} cy={ty} r={5} fill="white" stroke={color} strokeWidth={3} />
          </g>
        )}
        {[0, 0.5, 1].map((t) => {
          const [x, y] = polar(start + sweep * t, r - stroke - 6);
          return (
            <text key={t} x={x} y={y} textAnchor="middle" dominantBaseline="middle" className="fill-muted-foreground" fontSize={10}>
              {Math.round(max * t)}
            </text>
          );
        })}
        <text x={cx} y={cy + 2} textAnchor="middle" className="fill-foreground" fontSize={36} fontWeight={700} style={{ fontVariantNumeric: 'tabular-nums' }}>
          {fmt(tweened, decimals)}
        </text>
        <text x={cx} y={cy + 24} textAnchor="middle" className="fill-muted-foreground" fontSize={13}>
          {unit}
        </text>
      </svg>
      <div className="-mt-4 text-center">
        <div className="text-sm font-medium">{label}</div>
        {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
      </div>
    </div>
  );
}

function LoadBar({ value, max, label, warn = true, color = C_FREQ }: { value: number | null; max?: number | null; label?: string; warn?: boolean; color?: string }) {
  if (!isNum(value) || !isNum(max) || max <= 0) return null;
  const pct = (Math.abs(value) / max) * 100;
  const barColor = warn && pct > 100 ? VFD_STATUS_COLORS.critical : warn && pct > 90 ? VFD_STATUS_COLORS.warning : color;
  return (
    <div className="mt-3">
      <div className="mb-1 flex justify-between text-[11px] text-muted-foreground">
        <span>{label || 'Load'}</span>
        <span className="tabular-nums">
          {warn && pct > 90 && <AlertTriangle className="mr-1 inline h-3 w-3" style={{ color: barColor }} />}
          <AnimatedNumber value={pct} decimals={0} />% of {fmt(max, max < 10 ? 1 : 0)}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
        <motion.div
          className="h-full rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(100, pct)}%`, backgroundColor: barColor }}
          transition={{ type: 'spring', stiffness: 120, damping: 20 }}
        />
      </div>
    </div>
  );
}

function CalcBadge({ value }: { value?: VfdComputedValue | null }) {
  if (!value?.calc) return null;
  return (
    <span className="rounded border border-border px-1 py-px text-[10px] uppercase tracking-wide text-muted-foreground" title={value.item.note || 'Calculated from other readings'}>
      calc
    </span>
  );
}

function KpiTile({
  icon: Icon, label, value, unit, color, spark, online, children, sparkMinTop,
}: { icon: any; label: string; value?: VfdComputedValue | null; unit?: string; color: string; spark: (number | null)[]; online: boolean; children?: any; sparkMinTop?: number }) {
  const hasError = value?.state === 'error';
  const num = online && value && isNum(value.num) ? value.num : null;
  return (
    <Card className="group relative overflow-hidden border-border/60 transition-shadow hover:shadow-lg">
      <div className="absolute inset-x-0 top-0 h-0.5" style={{ backgroundColor: color, opacity: 0.8 }} />
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="rounded-md p-1" style={{ backgroundColor: `${color}1f`, color }}><Icon className="h-3.5 w-3.5" /></span>
            {label}
          </div>
          <CalcBadge value={value} />
        </div>
        <div className="mt-2 flex items-baseline gap-1.5" title={value?.error || ''}>
          {num !== null ? (
            <AnimatedNumber value={num} decimals={value?.item.decimals ?? 2} className="text-3xl font-bold tabular-nums" />
          ) : (
            <span className="text-3xl font-bold tabular-nums text-muted-foreground">{hasError ? 'ERR' : '—'}</span>
          )}
          <span className="text-sm text-muted-foreground">{unit ?? value?.item.unit ?? ''}</span>
        </div>
        {hasError && <div className="mt-1 truncate text-xs" style={{ color: VFD_STATUS_COLORS.critical }}>{value?.error}</div>}
        <div className="mt-2">{online ? <Sparkline data={spark} color={color} minTop={sparkMinTop} /> : <div className="h-8" />}</div>
        {children}
      </CardContent>
    </Card>
  );
}

function TrendChart({ data, series, unit, minTop }: { data: TrendPoint[]; series: (typeof TREND_SERIES)[number]; unit: string; minTop: number }) {
  const last = [...data].reverse().find((p) => isNum(p[series.key]))?.[series.key] ?? null;
  const vals = data.map((p) => p[series.key]).filter(isNum) as number[];
  const { top, bottom, step, ticks } = valueDomain(vals, minTop);
  const dec = tickDecimals(step);
  const gradientId = `vfd-grad-${series.key}`;
  return (
    <Card className="border-border/60">
      <CardContent className="p-4">
        <div className="mb-2 flex items-baseline justify-between">
          <div className="flex items-center gap-2 text-sm font-medium">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: series.color }} />
            {series.label}
          </div>
          <div className="text-sm text-muted-foreground"><AnimatedNumber value={last as number | null} decimals={2} className="tabular-nums" /> {unit}</div>
        </div>
        <div className="h-36">
          {data.length >= 2 && vals.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-1 text-center text-xs text-muted-foreground">
              <span className="font-medium text-foreground/80">No readings for {series.label.toLowerCase()}</span>
              <span>The drive isn’t returning this value — check this register in the admin panel.</span>
            </div>
          ) : data.length < 2 ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-xs text-muted-foreground">
              <div className="flex gap-1">
                {[0, 1, 2].map((i) => (
                  <motion.span key={i} className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: series.color }} animate={{ opacity: [0.2, 1, 0.2] }} transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }} />
                ))}
              </div>
              Collecting live samples…
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={series.color} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={series.color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="currentColor" strokeOpacity={0.08} vertical={false} />
                <XAxis
                  dataKey="t"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={(t) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  tick={{ fontSize: 10, fill: 'currentColor', opacity: 0.6 }}
                  stroke="currentColor"
                  strokeOpacity={0.15}
                  minTickGap={40}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: 'currentColor', opacity: 0.6 }}
                  stroke="currentColor"
                  strokeOpacity={0.15}
                  width={44}
                  domain={[bottom, top]}
                  ticks={ticks}
                  allowDataOverflow={false}
                  tickFormatter={(v: number) => v.toFixed(dec)}
                />
                <Tooltip
                  cursor={{ stroke: 'currentColor', strokeOpacity: 0.3 }}
                  contentStyle={{ background: 'var(--popover)', color: 'var(--popover-foreground)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                  labelFormatter={(t) => new Date(Number(t)).toLocaleTimeString()}
                  formatter={(v: any) => [`${fmt(Number(v), Math.max(2, dec + 1))} ${unit}`, series.label]}
                />
                <Area
                  type="monotoneX"
                  dataKey={series.key}
                  stroke={series.color}
                  strokeWidth={2}
                  fill={`url(#${gradientId})`}
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--background)' }}
                  isAnimationActive={false}
                  connectNulls
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------------ hero: VFD → cable → motor

/**
 * Schematic of the drive line. Every motion maps to a reading:
 *  - fan rotation speed  ∝ output frequency (spins up / coasts down smoothly)
 *  - energy flow on cable ∝ motor load (current vs rated, else power)
 *  - COMM LED blinks once per data packet received from the bridge
 */
function DriveLine({
  state, freqFrac, loadFrac, hzText, rpmText, kwText, packetTs,
}: { state: VfdDriveState; freqFrac: number; loadFrac: number; hzText: string; rpmText: string; kwText: string; packetTs?: number }) {
  const reduce = useReducedMotion();
  const rotorRef = useRef<SVGGElement>(null);
  const flowRef = useRef<SVGPathElement>(null);
  const targetRef = useRef({ spin: 0, flow: 0 });
  const running = state === 'running';
  const fault = state === 'fault';
  const accent = fault ? VFD_STATUS_COLORS.critical : running ? C_FREQ : VFD_STATUS_COLORS.neutral;

  // The fan and flow carry state (running vs stopped), so they keep moving under
  // "reduce motion" — just calmer. Purely decorative effects are skipped there.
  const calm = reduce ? 0.3 : 1;
  targetRef.current = {
    spin: running ? (120 + 900 * clamp01(freqFrac)) * calm : 0, // deg/s
    flow: running ? (25 + 140 * clamp01(loadFrac)) * calm : 0, // px/s
  };

  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    let angle = 0;
    let spin = 0;
    let offset = 0;
    let flow = 0;
    const tick = (now: number) => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      const k = 1 - Math.exp(-dt * 1.6); // inertia: ~1.5 s to reach a new speed
      spin += (targetRef.current.spin - spin) * k;
      flow += (targetRef.current.flow - flow) * k;
      angle = (angle + spin * dt) % 360;
      offset = (offset - flow * dt) % 1000;
      rotorRef.current?.setAttribute('transform', `rotate(${angle.toFixed(2)} 552 110)`);
      flowRef.current?.setAttribute('stroke-dashoffset', offset.toFixed(2));
      if (flowRef.current) flowRef.current.style.opacity = String(Math.min(1, 0.15 + flow / 60));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const led = (x: number, label: string, on: boolean, color: string, blinkKey?: number) => (
    <g key={label}>
      {blinkKey !== undefined && !reduce ? (
        <motion.circle key={blinkKey} cx={x} cy={170} r={5} initial={{ fill: color, opacity: 1 }} animate={{ opacity: 0.25 }} transition={{ duration: 0.6 }} />
      ) : (
        <circle cx={x} cy={170} r={5} fill={on ? color : 'currentColor'} opacity={on ? 1 : 0.15} className={on ? '' : 'text-foreground'} />
      )}
      {on && blinkKey === undefined && <circle cx={x} cy={170} r={9} fill={color} opacity={0.2} />}
      <text x={x} y={188} textAnchor="middle" fontSize={8} className="fill-muted-foreground">{label}</text>
    </g>
  );

  return (
    <svg viewBox="0 0 640 210" className="h-full w-full" role="img" aria-label={`Drive ${STATE_META[state].label}: ${hzText} Hz, ${rpmText} rpm, ${kwText} kW`}>
      <defs>
        <linearGradient id="vfd-body" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity={0.14} />
          <stop offset="100%" stopColor="currentColor" stopOpacity={0.05} />
        </linearGradient>
        <radialGradient id="rotor-glow">
          <stop offset="0%" stopColor={accent} stopOpacity={0.35} />
          <stop offset="100%" stopColor={accent} stopOpacity={0} />
        </radialGradient>
      </defs>

      {/* inverter */}
      <g className="text-foreground">
        <rect x={20} y={22} width={150} height={176} rx={14} fill="url(#vfd-body)" stroke="currentColor" strokeOpacity={0.25} />
        <text x={95} y={44} textAnchor="middle" fontSize={11} fontWeight={700} className="fill-muted-foreground" letterSpacing={2}>VFD</text>
        <rect x={36} y={56} width={118} height={58} rx={6} fill="currentColor" fillOpacity={0.08} stroke={accent} strokeOpacity={0.45} />
        <text x={95} y={92} textAnchor="middle" fontSize={24} fontWeight={700} fontFamily="ui-monospace, monospace" fill={accent} style={{ fontVariantNumeric: 'tabular-nums' }}>{hzText}</text>
        <text x={147} y={108} textAnchor="end" fontSize={9} className="fill-muted-foreground">Hz</text>
        {[0, 1, 2, 3].map((i) => (
          <rect key={i} x={40 + i * 30} y={126} width={22} height={9} rx={2} fill="currentColor" opacity={0.12} />
        ))}
        {led(60, 'RUN', running, VFD_STATUS_COLORS.good)}
        {led(95, 'FLT', fault, VFD_STATUS_COLORS.critical)}
        {packetTs ? led(130, 'COM', true, VFD_STATUS_COLORS.warning, packetTs) : led(130, 'COM', false, VFD_STATUS_COLORS.warning)}
      </g>

      {/* cable + energy flow */}
      <path d="M170 110 C 250 110, 300 140, 380 110 S 440 110, 470 110" fill="none" stroke="currentColor" strokeOpacity={0.18} strokeWidth={10} strokeLinecap="round" className="text-foreground" />
      <path ref={flowRef} d="M170 110 C 250 110, 300 140, 380 110 S 440 110, 470 110" fill="none" stroke={accent} strokeWidth={4} strokeLinecap="round" strokeDasharray="10 16" style={{ opacity: running ? 1 : 0.15 }} />
      <g>
        <rect x={276} y={136} width={92} height={24} rx={12} fill="currentColor" fillOpacity={0.08} className="text-foreground" stroke={C_POWER} strokeOpacity={0.5} />
        <text x={322} y={152} textAnchor="middle" fontSize={11} fontWeight={600} fill={C_POWER} style={{ fontVariantNumeric: 'tabular-nums' }}>{kwText} kW</text>
      </g>

      {/* motor */}
      <g className="text-foreground">
        <rect x={470} y={78} width={18} height={64} rx={4} fill="currentColor" opacity={0.22} />
        <rect x={488} y={62} width={64} height={96} rx={10} fill="url(#vfd-body)" stroke="currentColor" strokeOpacity={0.25} />
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <line key={i} x1={498 + i * 9} y1={68} x2={498 + i * 9} y2={152} stroke="currentColor" strokeOpacity={0.15} strokeWidth={3} strokeLinecap="round" />
        ))}
        <rect x={500} y={48} width={30} height={16} rx={3} fill="currentColor" opacity={0.18} />
        <rect x={474} y={154} width={84} height={8} rx={3} fill="currentColor" opacity={0.15} />
      </g>
      <circle cx={552} cy={110} r={54} fill="url(#rotor-glow)" opacity={running || fault ? 1 : 0} />
      <circle cx={552} cy={110} r={40} fill="currentColor" fillOpacity={0.06} stroke={accent} strokeOpacity={0.6} strokeWidth={2} className="text-foreground" />
      <g ref={rotorRef}>
        {[0, 60, 120, 180, 240, 300].map((a) => (
          <path key={a} d="M552 110 C 556 92, 572 80, 584 86 C 590 92, 574 104, 552 110 Z" fill={accent} opacity={0.85} transform={`rotate(${a} 552 110)`} />
        ))}
      </g>
      <circle cx={552} cy={110} r={7} fill="currentColor" className="text-foreground" opacity={0.6} />
      <text x={552} y={178} textAnchor="middle" fontSize={12} fontWeight={600} className="fill-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>{rpmText} rpm</text>
      {fault && !reduce && (
        <motion.circle cx={552} cy={110} r={44} fill="none" stroke={VFD_STATUS_COLORS.critical} strokeWidth={3} animate={{ opacity: [0.9, 0.1, 0.9], r: [44, 52, 44] }} transition={{ duration: 1.4, repeat: Infinity }} />
      )}
    </svg>
  );
}

// ------------------------------------------------------------------ main

export function VFDDrivesPanel({ clientId, token, initialDrives }: VFDDrivesPanelProps) {
  const [drives, setDrives] = useState<VfdDrive[]>(initialDrives || []);
  const [loading, setLoading] = useState(!initialDrives);
  const [selectedId, setSelectedId] = useState<string | null>(initialDrives?.[0]?.id ?? null);
  const [live, setLive] = useState<Record<string, LiveSnapshot>>({});
  const [status, setStatus] = useState<Record<string, RuntimeStatus>>({});
  const [, setTick] = useState(0);
  const trendsRef = useRef<Record<string, TrendPoint[]>>({});
  const lastGoodRef = useRef<Record<string, Record<string, { value: number; raw?: number; at: number }>>>({});
  const drivesRef = useRef<VfdDrive[]>(drives);
  drivesRef.current = drives;

  useEffect(() => {
    if (initialDrives) return;
    let cancelled = false;
    vfdApi.listForClient(token, clientId)
      .then((list) => {
        if (cancelled) return;
        setDrives(list);
        setSelectedId((prev) => prev ?? list[0]?.id ?? null);
      })
      .catch(() => { /* empty state handles it */ })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [clientId, token, initialDrives]);

  // Re-render every second so "x s ago" and freshness stay honest between packets.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const ws = getWebSocketClient(token, { clientId });

    const onLive = (data: any) => {
      const id = data?.vfdId;
      if (!id) return;
      // Receipt time, not the bridge PC clock: a bridge clock a few seconds off made
      // every packet look old ("4s ago") or even stale.
      const ts = Date.now();
      const values: Record<string, number | null> = { ...(data.values || {}) };
      const raws: Record<string, number> = { ...(data.raws || {}) };
      const errors: Record<string, string> = { ...(data.errors || {}) };
      const good = (lastGoodRef.current[id] ||= {});
      const names = new Set([...Object.keys(values), ...Object.keys(errors)]);
      let missing = 0;
      for (const name of names) {
        const v = values[name];
        if (isNum(v)) { good[name] = { value: v, raw: raws[name], at: ts }; continue; }
        const held = good[name];
        if (held && ts - held.at < HOLD_LAST_GOOD_MS) {
          values[name] = held.value;
          if (held.raw !== undefined) raws[name] = held.raw;
          delete errors[name];
        } else {
          missing++;
        }
      }
      // Recompute the link state from what is actually shown after holding.
      const comm = names.size === 0 ? (data.comm || 'ok') : missing === 0 ? 'ok' : missing < names.size ? 'partial' : 'error';
      data = { ...data, values, errors };
      setLive((prev) => ({
        ...prev,
        [id]: { values, raws, errors, comm, timestamp: ts },
      }));

      const drive = drivesRef.current.find((d) => d.id === id);
      if (drive) {
        const computed = computeVfdValues(drive.items, data.values, data.errors);
        const num = (role: any) => findByRole(drive.items, computed, role)?.num ?? null;
        const point: TrendPoint = { t: ts, frequency: num('frequency'), current: num('current'), power: num('power'), voltage: num('voltage'), pf: num('pf') };
        const cutoff = ts - TREND_WINDOW_MS;
        const next = [...(trendsRef.current[id] || []), point].filter((p) => p.t >= cutoff);
        trendsRef.current[id] = next.length > MAX_TREND_POINTS ? next.slice(-MAX_TREND_POINTS) : next;
      }
    };

    const onStatus = (data: any) => {
      const id = data?.vfdId;
      if (!id) return;
      setStatus((prev) => {
        const cur = prev[id];
        const next: RuntimeStatus = { connected: !!data.connected, comm: data.comm, error: data.error, reconnecting: !!data.reconnecting, at: Date.now() };
        if (cur && cur.connected === next.connected && cur.comm === next.comm && cur.error === next.error && Date.now() - cur.at < 4000) return prev;
        return { ...prev, [id]: next };
      });
    };

    ws.on('vfd_live_data', onLive);
    ws.on('vfd_status_update', onStatus);
    ws.connect();
    return () => {
      ws.off('vfd_live_data', onLive);
      ws.off('vfd_status_update', onStatus);
    };
  }, [clientId, token]);

  // Recomputed every render (the 1s tick keeps freshness honest); drive counts are small.
  const driveViews = drives.map((drive) => {
    const snap = live[drive.id];
    const st = status[drive.id];
    const computed = computeVfdValues(drive.items, snap?.values, snap?.errors);
    // 15s floor covers simulation mode's 5s cadence and a slow server tick.
    const staleAfter = Math.max(15000, (drive.pollIntervalMs || 1000) * 6);
    const fresh = !!snap && Date.now() - snap.timestamp < staleAfter;
    const answering = fresh && snap.comm !== 'error';
    const online = answering && st?.connected !== false;
    const state = getDriveState(drive.items, computed, online, !!snap || !!st);
    return { drive, snap, st, computed, online, state };
  });

  const selected = driveViews.find((v) => v.drive.id === selectedId) || driveViews[0];

  if (loading) {
    return (
      <div className="flex h-72 items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading drives…
      </div>
    );
  }

  if (drives.length === 0) {
    return (
      <Card className="p-12 text-center">
        <GaugeIcon className="mx-auto mb-4 h-14 w-14 text-muted-foreground opacity-50" />
        <h3 className="mb-2 text-xl font-semibold">No VFD drives configured</h3>
        <p className="text-muted-foreground">Drives are added by your administrator in the Admin Panel → VFD Drives.</p>
      </Card>
    );
  }

  const counts = driveViews.reduce((acc, v) => { acc[v.state] = (acc[v.state] || 0) + 1; return acc; }, {} as Record<string, number>);
  const totalKw = driveViews.reduce((sum, v) => {
    const p = findByRole(v.drive.items, v.computed, 'power')?.num;
    return v.online && isNum(p) ? sum + p : sum;
  }, 0);
  const faults = counts.fault || 0;
  const noComms = (counts.offline || 0) + (counts.waiting || 0);

  const summary = [
    { label: 'Drives', value: drives.length, decimals: 0, icon: GaugeIcon, color: undefined as string | undefined, alert: false },
    { label: 'Running', value: counts.running || 0, decimals: 0, icon: PlayCircle, color: VFD_STATUS_COLORS.good, alert: false },
    { label: 'Fault', value: faults, decimals: 0, icon: AlertTriangle, color: faults > 0 ? VFD_STATUS_COLORS.critical : undefined, alert: faults > 0 },
    { label: 'No comms', value: noComms, decimals: 0, icon: WifiOff, color: (counts.offline || 0) > 0 ? VFD_STATUS_COLORS.warning : undefined, alert: false },
    { label: 'Total output', value: totalKw, decimals: 1, unit: 'kW', icon: Zap, color: C_POWER, alert: false },
  ];

  return (
    <div className="space-y-6">
      {/* Fleet summary */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {summary.map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05, duration: 0.3 }}>
            <Card className={`relative overflow-hidden border-border/60 ${s.alert ? 'ring-1 ring-[#d03b3b]/50' : ''}`}>
              {s.alert && <div className="pointer-events-none absolute inset-0 animate-pulse bg-[#d03b3b]/10 motion-reduce:animate-none" />}
              <CardContent className="relative flex items-center gap-3 p-4">
                <div className="rounded-lg bg-muted p-2" style={s.color ? { color: s.color, backgroundColor: `${s.color}1a` } : undefined}>
                  <s.icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">{s.label}</div>
                  <div className="text-xl font-bold tabular-nums">
                    <AnimatedNumber value={s.value} decimals={s.decimals} />
                    {s.unit && <span className="ml-1 text-sm font-medium text-muted-foreground">{s.unit}</span>}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Drive selector */}
      {drives.length > 1 && (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {driveViews.map((v, i) => {
            const isSel = v.drive.id === selected?.drive.id;
            const f = findByRole(v.drive.items, v.computed, 'frequency');
            const a = findByRole(v.drive.items, v.computed, 'current');
            const p = findByRole(v.drive.items, v.computed, 'power');
            const ratedI = v.drive.ratings?.ratedCurrent;
            const load = v.online && isNum(a?.num) && isNum(ratedI) && ratedI > 0 ? clamp01(a!.num! / ratedI) : null;
            const meta = STATE_META[v.state];
            return (
              <motion.button
                key={v.drive.id}
                type="button"
                onClick={() => setSelectedId(v.drive.id)}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.05 }}
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.98 }}
                className="relative overflow-hidden rounded-xl border border-border/60 bg-card p-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                aria-pressed={isSel}
              >
                {isSel && (
                  <motion.div layoutId="vfd-selected" className="pointer-events-none absolute inset-0 rounded-xl ring-2 ring-primary" style={{ boxShadow: '0 0 24px -6px var(--primary)' }} transition={{ type: 'spring', stiffness: 380, damping: 32 }} />
                )}
                <div className="relative flex items-start justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <MiniFan spinning={v.state === 'running'} color={meta.color} />
                    <div className="min-w-0">
                      <div className="truncate font-semibold">{v.drive.name}</div>
                      <div className="truncate text-xs text-muted-foreground">{v.drive.location || [v.drive.brand, v.drive.modelName].filter(Boolean).join(' · ') || '—'}</div>
                    </div>
                  </div>
                  <StatePill state={v.state} />
                </div>
                <div className="relative mt-3 grid grid-cols-3 gap-2 text-center">
                  {[{ v: f, u: 'Hz', d: 1 }, { v: a, u: 'A', d: 1 }, { v: p, u: 'kW', d: 2 }].map((x, j) => (
                    <div key={j} className="rounded-md bg-muted/60 px-1 py-1.5">
                      <div className="text-sm font-semibold tabular-nums">
                        {v.online && isNum(x.v?.num) ? <AnimatedNumber value={x.v!.num} decimals={x.d} /> : '—'}
                      </div>
                      <div className="text-[10px] text-muted-foreground">{x.u}</div>
                    </div>
                  ))}
                </div>
                <div className="relative mt-3 h-1 overflow-hidden rounded-full bg-muted">
                  <motion.div className="h-full rounded-full" style={{ backgroundColor: C_CURRENT }} initial={{ width: 0 }} animate={{ width: `${(load ?? 0) * 100}%` }} transition={{ type: 'spring', stiffness: 120, damping: 20 }} />
                </div>
              </motion.button>
            );
          })}
        </div>
      )}

      <AnimatePresence mode="wait">
        {selected && (
          <motion.div key={selected.drive.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.2 }}>
            <DriveDetail
              view={selected}
              trend={trendsRef.current[selected.drive.id] || []}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ------------------------------------------------------------------ detail

function DriveDetail({
  view, trend,
}: {
  view: { drive: VfdDrive; snap?: LiveSnapshot; st?: RuntimeStatus; computed: Record<string, VfdComputedValue>; online: boolean; state: VfdDriveState };
  trend: TrendPoint[];
}) {
  const { drive, snap, st, computed, online, state } = view;
  const items = drive.items;
  const r = drive.ratings || {};
  const get = (role: any) => findByRole(items, computed, role);
  const freq = get('frequency');
  const rpm = get('rpm');
  const volt = get('voltage');
  const amp = get('current');
  const kw = get('power');
  const pf = get('pf');
  const fault = get('fault');
  const run = get('run');
  const n = (v?: VfdComputedValue | null) => (online && v && isNum(v.num) ? v.num : null);

  const maxFreq = r.maxFrequency || 50;
  const rpmMax = r.ratedRpm || Math.max(1500, Math.ceil(((rpm?.num || 0) * 1.2) / 100) * 100);
  const coreRoles = new Set(['frequency', 'rpm', 'voltage', 'current', 'power', 'pf', 'run', 'fault']);
  const others = items.filter((it) => !it.hidden && !coreRoles.has(it.role));
  const units: Record<string, string> = { frequency: freq?.item.unit || 'Hz', current: amp?.item.unit || 'A', power: kw?.item.unit || 'kW' };

  // Motor load for the third gauge + cable flow: current vs rated, else power vs rated.
  const loadPct = isNum(n(amp)) && r.ratedCurrent ? (n(amp)! / r.ratedCurrent) * 100
    : isNum(n(kw)) && r.ratedKw ? (n(kw)! / r.ratedKw) * 100 : null;
  const freqFrac = isNum(n(freq)) ? Math.abs(n(freq)!) / maxFreq : 0;
  const loadFrac = isNum(loadPct) ? loadPct / 100 : freqFrac * 0.7;
  const loadColor = isNum(loadPct) && loadPct > 100 ? VFD_STATUS_COLORS.critical : isNum(loadPct) && loadPct > 90 ? VFD_STATUS_COLORS.warning : C_CURRENT;

  const commLabel = online
    ? (snap?.comm === 'partial' ? 'Live · some registers failing' : 'Live')
    : st?.reconnecting ? 'Reconnecting…' : snap ? 'No response from drive' : 'Waiting for bridge';
  const commColor = online ? (snap?.comm === 'partial' ? VFD_STATUS_COLORS.warning : VFD_STATUS_COLORS.good) : VFD_STATUS_COLORS.warning;
  const spark = (key: keyof TrendPoint) => trend.map((p) => p[key] as number | null);
  // Minimum chart height per measure (~10% of rating) so near-zero noise stays near zero.
  const minTops = {
    frequency: maxFreq * 0.1,
    current: r.ratedCurrent ? r.ratedCurrent * 0.1 : 1,
    power: r.ratedKw ? r.ratedKw * 0.1 : 1,
    voltage: r.ratedVoltage ? r.ratedVoltage * 0.1 : 10,
    pf: 1,
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <Section i={0}>
        <Card className="relative overflow-hidden border-border/60">
          <div className="absolute inset-y-0 left-0 w-1" style={{ backgroundColor: STATE_META[state].color }} />
          <CardContent className="flex flex-col gap-4 p-5 pl-6 md:flex-row md:items-center md:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="truncate text-2xl font-bold">{drive.name}</h2>
                <StatePill state={state} size="lg" />
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                {(drive.brand || drive.modelName) && <span>{[drive.brand, drive.modelName].filter(Boolean).join(' · ')}</span>}
                {drive.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{drive.location}</span>}
                {drive.description && <span className="truncate">{drive.description}</span>}
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-3 text-sm">
              <span className="inline-flex items-center gap-2 rounded-full border px-3 py-1" style={{ color: commColor, borderColor: `${commColor}55` }} title={st?.error || ''}>
                {online ? (
                  <span className="relative flex h-2 w-2">
                    <motion.span key={snap?.timestamp} className="absolute inline-flex h-full w-full rounded-full" style={{ backgroundColor: commColor }} initial={{ scale: 1, opacity: 0.8 }} animate={{ scale: 3, opacity: 0 }} transition={{ duration: 0.9 }} />
                    <span className="relative inline-flex h-2 w-2 rounded-full" style={{ backgroundColor: commColor }} />
                  </span>
                ) : <WifiOff className="h-3.5 w-3.5" />}
                {commLabel}
              </span>
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <Clock className="h-3.5 w-3.5" /> {timeAgo(snap?.timestamp)}
              </span>
            </div>
          </CardContent>
        </Card>
      </Section>

      {/* Fault / comm banners */}
      <AnimatePresence>
        {online && fault?.flag && (
          <motion.div
            key="fault"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="relative flex items-start gap-3 overflow-hidden rounded-lg border p-4" style={{ borderColor: `${VFD_STATUS_COLORS.critical}66`, backgroundColor: `${VFD_STATUS_COLORS.critical}12` }} role="alert">
              <motion.div animate={{ rotate: [0, -12, 12, -8, 8, 0] }} transition={{ duration: 0.6, repeat: Infinity, repeatDelay: 2.4 }}>
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" style={{ color: VFD_STATUS_COLORS.critical }} />
              </motion.div>
              <div>
                <div className="font-semibold" style={{ color: VFD_STATUS_COLORS.critical }}>Drive fault — {fault.text}</div>
                <div className="text-sm text-muted-foreground">
                  {isNum(fault.code) ? `Fault code ${fault.code}. ` : ''}Check the drive keypad / manual for the fault description and reset the drive locally.
                </div>
              </div>
            </div>
          </motion.div>
        )}
        {!online && (st?.error || snap) && (
          <motion.div key="comm" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
            <div className="flex items-start gap-3 rounded-lg border p-4" style={{ borderColor: `${VFD_STATUS_COLORS.warning}66`, backgroundColor: `${VFD_STATUS_COLORS.warning}10` }}>
              <WifiOff className="mt-0.5 h-5 w-5 shrink-0" style={{ color: VFD_STATUS_COLORS.warning }} />
              <div className="text-sm">
                <div className="font-semibold">Not receiving data from this drive</div>
                <div className="text-muted-foreground">{st?.error || 'Last reading is stale.'} Values below are hidden until communication returns.</div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero: drive line + state */}
      <Section i={1} className="grid gap-4 lg:grid-cols-5">
        <Card className="border-border/60 lg:col-span-3">
          <CardContent className="p-4">
            <div className="mb-1 flex items-center justify-between text-sm">
              <span className="font-medium">Drive line</span>
              <span className="text-xs text-muted-foreground">Fan speed follows output frequency · flow follows motor load</span>
            </div>
            <div className="h-[200px]">
              <DriveLine
                state={state}
                freqFrac={freqFrac}
                loadFrac={loadFrac}
                hzText={online ? fmt(n(freq), 1) : '--.-'}
                rpmText={online ? fmt(n(rpm), 0) : '—'}
                kwText={online ? fmt(n(kw), 2) : '—'}
                packetTs={online ? snap?.timestamp : undefined}
              />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/60 lg:col-span-2">
          <CardContent className="flex h-full flex-col justify-between gap-4 p-5">
            <div>
              <div className="text-sm text-muted-foreground">Run state</div>
              <div className="mt-3 flex items-center gap-4">
                <div className="relative grid h-14 w-14 place-items-center rounded-full" style={{ backgroundColor: `${STATE_META[state].color}1a` }}>
                  {(state === 'running' || state === 'fault') && (
                    <span className="absolute inset-0 animate-ping rounded-full opacity-30 motion-reduce:hidden" style={{ backgroundColor: STATE_META[state].color, animationDuration: '2s' }} />
                  )}
                  {(() => {
                    const Icon = STATE_META[state].icon;
                    return <Icon className={`relative h-7 w-7 ${state === 'waiting' ? 'animate-spin' : ''}`} style={{ color: STATE_META[state].color }} />;
                  })()}
                </div>
                <div>
                  <AnimatePresence mode="wait">
                    <motion.div key={online && run ? run.text : STATE_META[state].label} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} className="text-2xl font-bold">
                      {online && run ? run.text : STATE_META[state].label}
                    </motion.div>
                  </AnimatePresence>
                  {online && run?.calc && <div className="text-xs text-muted-foreground">Derived from output frequency</div>}
                </div>
              </div>
            </div>
            <div className="space-y-2.5 border-t border-border/60 pt-4 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Fault status</span>
                {online && fault ? (
                  <span className="inline-flex items-center gap-1.5 font-semibold" style={{ color: fault.flag ? VFD_STATUS_COLORS.critical : VFD_STATUS_COLORS.good }}>
                    {fault.flag ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                    {fault.text}
                  </span>
                ) : <span className="text-muted-foreground">—</span>}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Link</span>
                <span>{drive.connectionType === 'profinet' ? 'PROFINET (Ethernet)' : drive.connectionType === 'tcp' ? 'Modbus TCP' : 'Modbus RTU (RS-485)'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Update rate</span>
                <span>{((drive.pollIntervalMs || 1000) / 1000).toFixed(1)} s</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </Section>

      {/* Gauges */}
      <Section i={2} className="grid gap-4 md:grid-cols-3">
        <Card className="border-border/60">
          <CardContent className="p-5">
            <ArcGauge id="freq" value={n(freq)} max={maxFreq} label="Output frequency" unit={units.frequency} decimals={freq?.item.decimals ?? 2} color={C_FREQ} sub={`0 – ${maxFreq} Hz`} />
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-5">
            <ArcGauge id="rpm" value={n(rpm)} max={rpmMax} label="Motor speed" unit={rpm?.item.unit || 'rpm'} decimals={0} color={C_SPEED} sub={rpm?.calc ? 'Calculated from frequency' : r.ratedRpm ? `Rated ${r.ratedRpm} rpm` : undefined} />
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-5">
            {isNum(loadPct) || r.ratedCurrent || r.ratedKw ? (
              <ArcGauge id="load" value={online ? loadPct : null} max={120} label="Motor load" unit="%" decimals={0} color={loadColor} sub={r.ratedCurrent ? `Current vs rated ${r.ratedCurrent} A` : `Power vs rated ${r.ratedKw} kW`} />
            ) : (
              <ArcGauge id="pf" value={n(pf)} max={1} label="Power factor" unit="cos φ" decimals={2} color={C_POWER} sub="Set motor ratings to see % load" />
            )}
          </CardContent>
        </Card>
      </Section>

      {/* Electrical KPIs */}
      <Section i={3} className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiTile icon={Zap} label="Output voltage" value={volt} unit={volt?.item.unit || 'V'} color={C_SPEED} spark={spark('voltage')} sparkMinTop={minTops.voltage} online={online}>
          <LoadBar value={n(volt)} max={r.ratedVoltage} label="Of rated voltage" warn={false} color={C_SPEED} />
        </KpiTile>
        <KpiTile icon={Activity} label="Output current" value={amp} unit={amp?.item.unit || 'A'} color={C_CURRENT} spark={spark('current')} sparkMinTop={minTops.current} online={online}>
          <LoadBar value={n(amp)} max={r.ratedCurrent} label="Motor load" color={C_CURRENT} />
        </KpiTile>
        <KpiTile icon={Zap} label="Output power" value={kw} unit={kw?.item.unit || 'kW'} color={C_POWER} spark={spark('power')} sparkMinTop={minTops.power} online={online}>
          <LoadBar value={n(kw)} max={r.ratedKw} label="Of rated power" color={C_POWER} />
        </KpiTile>
        <KpiTile icon={GaugeIcon} label="Power factor" value={pf} unit="" color={C_FREQ} spark={spark('pf')} sparkMinTop={minTops.pf} online={online}>
          <LoadBar value={n(pf)} max={1} label="cos φ" warn={false} color={C_FREQ} />
        </KpiTile>
      </Section>

      {/* Trends — one measure per chart */}
      <Section i={4}>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold">Live trend</h3>
          <span className="text-xs text-muted-foreground">Last 10 minutes · this session</span>
        </div>
        <div className="grid gap-4 lg:grid-cols-3">
          {TREND_SERIES.map((s) => (
            <TrendChart key={s.key} data={trend} series={s} unit={units[s.key]} minTop={minTops[s.key]} />
          ))}
        </div>
      </Section>

      {/* Other parameters */}
      {others.length > 0 && (
        <Section i={5}>
          <h3 className="mb-2 text-sm font-semibold">Other parameters</h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {others.map((it) => {
              const v = computed[it.name];
              const flagColor = v?.flag !== undefined ? (v.state === 'bad' ? VFD_STATUS_COLORS.critical : v.state === 'good' ? VFD_STATUS_COLORS.good : undefined) : undefined;
              const isFlag = v?.flag !== undefined;
              return (
                <Card key={it.id || it.name} className="border-border/60 transition-shadow hover:shadow-md">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-xs text-muted-foreground">{it.name}</span>
                      <CalcBadge value={v} />
                    </div>
                    <div className="mt-1 flex items-baseline gap-1" title={v?.error || ''}>
                      {online && !isFlag && isNum(v?.num) ? (
                        <AnimatedNumber value={v!.num} decimals={it.decimals ?? 2} className="text-xl font-semibold tabular-nums" />
                      ) : (
                        <span className="text-xl font-semibold tabular-nums" style={flagColor ? { color: flagColor } : undefined}>{online ? v?.text ?? '—' : '—'}</span>
                      )}
                      <span className="text-xs text-muted-foreground">{it.unit}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </Section>
      )}

    </div>
  );
}
